import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { API_URL } from '../lib/http'

/* The storefront's live connection to the floor.

   The console can sell a car, change its variant-year quote or put a new one out at any
   moment, and until now the site went on advertising the old floor until the
   browser's five-minute cache expired — long enough for somebody to open a
   car that had already gone.

   What arrives is an event, not data: the frame carries a car's id and the
   cache is dropped, so the rows come back down the path they always do.
   Writing the car straight into the cache would mean two ways for it to reach
   the page, and the rarer one would be the one that broke.

   The stream is the public one (see api/src/utils/events.ts): it carries the
   vehicle and variant catalogue events and nothing about a customer. Mounted once, in the Layout,
   so it is open on every page rather than only while the grid happens to be
   on screen — the detail page a visitor is reading is exactly where a car
   being sold matters most.

   EventSource reconnects by itself, so there is no retry here to get wrong. */

const CATALOGUE_EVENTS = [
  'vehicle:created',
  'vehicle:updated',
  'vehicle:deleted',
  'variant:created',
  'variant:updated',
  'variant:deleted',
]

export function useCatalogueStream() {
  const client = useQueryClient()

  useEffect(() => {
    /* Vite dev server and the API are different origins, and this is the one
       request the browser makes that axios does not — EventSource takes an
       absolute URL rather than the http instance's baseURL. */
    const source = new EventSource(`${API_URL}/api/stream/catalogue`)

    /* Every key the catalogue is cached under starts with 'vehicles' — the
       lists, the rails, the facets and each car's own page — so one prefix
       drops the lot. Anything currently on screen refetches; anything not is
       simply asked for again the next time it is. */
    const refresh = () => client.invalidateQueries({ queryKey: ['vehicles'] })

    for (const event of CATALOGUE_EVENTS) source.addEventListener(event, refresh)

    /* The same trick for the page itself. Somebody in the console reordering
       the home page, rewriting a heading or switching a band off is a change
       every open storefront should pick up — the stream carries the page id,
       and the layout is refetched down the path it normally comes. */
    source.addEventListener('cms:updated', () => client.invalidateQueries({ queryKey: ['cms'] }))

    /* And for the questions, which are records of their own rather than part
       of the layout: a new answer filed at the desk should be readable on the
       page somebody already has open. */
    source.addEventListener('faqs:updated', () =>
      client.invalidateQueries({ queryKey: ['faqs'] }),
    )

    /* A stream that drops is a stream that reconnects, and the floor may well
       have changed while it was down — so a reconnection is itself a reason
       to ask again rather than something to log. Only a reconnection: the
       first open happens beside the page's own first request, and dropping
       the cache there would fetch the floor twice on every visit. */
    let opened = false
    source.addEventListener('open', () => {
      if (opened) refresh()
      opened = true
    })

    return () => source.close()
  }, [client])
}

export default useCatalogueStream
